/**
 * Dashboard briefing — the AI-first greeting card.
 *
 * Backed by ``GET /api/v1/dashboard/briefing`` which assembles a
 * role-aware payload from DB queries (no Claude calls). Cached server-
 * side per-(org, user) for 5 minutes; mutation endpoints invalidate on
 * the way through.
 */
import client from "./client";

export type BriefSeverity = "urgent" | "review" | "info";

export interface BriefItem {
  text: string;
  url: string | null;
  severity: BriefSeverity;
}

export interface BriefingGreeting {
  /** "Good morning, Angela" */
  headline: string;
  /** Optional secondary line, e.g. "Here's your week ahead." */
  subline: string | null;
  /** IANA timezone string used to compute the greeting (for diagnostics). */
  timezone: string;
}

export interface BriefingStats {
  total_employees: number | null;
  pending_approvals: number | null;
  open_positions: number | null;
  /** 0..1 — UI multiplies by 100 and rounds for display. */
  compliance_score: number | null;
}

export type AlertSeverity = "red" | "amber" | "green";

export interface DashboardTodo {
  id: string;
  text: string;
  type: string;
  completed: boolean;
  link: string | null;
}

export interface DashboardAlert {
  severity: AlertSeverity;
  text: string;
  link: string | null;
}

export interface AgentSummary {
  status: string;
  summary: string;
  last_run: string | null;
}

export interface NextMeeting {
  title: string;
  time: string;
  context: string;
  attendees: { name: string; initials: string }[];
}

export interface DashboardBriefing {
  /** Resolved role used to pick the bundle. Helpful for SPA branching. */
  role: "HR_ADMIN" | "SUPER_ADMIN" | "MANAGER" | "EMPLOYEE";
  greeting: BriefingGreeting;
  urgent: BriefItem[];
  to_review: BriefItem[];
  this_week: BriefItem[];
  stats: BriefingStats & {
    employee_count?: number;
    employee_delta?: string;
    pending_count?: number;
    pending_urgent?: number;
    open_roles?: number;
    applicant_count?: number;
    compliance_delta?: string;
  };
  /** True when the org has zero active employees — SPA shows the
   *  "welcome to Lenavio" setup steps instead of the regular bands. */
  empty_state: boolean;
  generated_at: string;
  // ── Command-center extension fields ──
  user_name?: string;
  greeting_time?: "morning" | "afternoon" | "evening";
  today?: string;
  pending_approvals?: number;
  sentinel_findings?: number;
  todo_count?: number;
  todos?: DashboardTodo[];
  alerts?: DashboardAlert[];
  agents?: {
    atlas?: AgentSummary;
    sentinel?: AgentSummary;
    pilot?: AgentSummary;
  };
  next_meeting?: NextMeeting | null;
}

export async function getDashboardBriefing(): Promise<DashboardBriefing> {
  // Pass the browser's IANA timezone so the server can render the
  // greeting in the user's actual local time. Without this, a user
  // in PST at 9pm gets the org-default (often UTC) greeting which
  // reads "good morning" — the bug we're fixing here. Resolves to
  // values like "America/New_York", "Europe/London", "Asia/Tokyo".
  // Browsers without ``Intl`` support (vanishingly rare) get no
  // header and the server falls back to org tz.
  let tz: string | undefined;
  try {
    tz = Intl.DateTimeFormat().resolvedOptions().timeZone || undefined;
  } catch {
    tz = undefined;
  }
  const res = await client.get<DashboardBriefing>("/dashboard/briefing", {
    params: tz ? { tz } : undefined,
  });
  return res.data;
}
